import React, { useState } from 'react';
import { 
  Globe, Sparkles, RefreshCw, CheckCircle2, AlertCircle, 
  Building2, Award, Users, Briefcase, FileCode2, ArrowRight, 
  Plus, Check, ExternalLink, ShieldCheck, ChevronDown, ChevronUp, Copy,
  Linkedin, Megaphone, BookOpen, FileText, Layers, Download, X,
  GraduationCap, TrendingUp, BookmarkCheck
} from 'lucide-react';
import { 
  WebsiteIntelligence, 
  LinkedInIntelligence, 
  PartnerOnIntelligence,
  PartnerOnCaseStudy,
  PartnerOnEBook,
  PartnerOnSolutionBrief,
  PartnerOnCollateral
} from '../types/websiteIntelligence';
import { db, doc, setDoc } from '../firebase';

interface WebsiteIntelligenceSectionProps {
  user: any;
  vendorProfile: any;
  onApplyNaics?: (codes: string[]) => void;
  onApplyNigp?: (codes: string[]) => void;
  onApplyCapabilities?: (caps: string[]) => void;
  onProfileUpdated?: (updatedProfile: any) => void;
}

export const WebsiteIntelligenceSection: React.FC<WebsiteIntelligenceSectionProps> = ({
  user,
  vendorProfile,
  onApplyNaics,
  onApplyNigp,
  onApplyCapabilities,
  onProfileUpdated
}) => {
  // Source Mode: 'website' | 'linkedin' | 'partneron'
  const [sourceMode, setSourceMode] = useState<'website' | 'linkedin' | 'partneron'>('website');

  // Website State
  const [websiteUrl, setWebsiteUrl] = useState<string>(vendorProfile?.website || '');
  const [isExtractingWebsite, setIsExtractingWebsite] = useState<boolean>(false);
  const [websiteActiveTab, setWebsiteActiveTab] = useState<'overview' | 'capabilities' | 'pastPerformance' | 'leadership' | 'codes'>('overview');

  // LinkedIn State
  const [linkedinCompanyUrl, setLinkedinCompanyUrl] = useState<string>(
    vendorProfile?.linkedinUrl || vendorProfile?.linkedInIntelligence?.companyPageUrl || ''
  );
  const [linkedinProfileUrls, setLinkedinProfileUrls] = useState<string[]>(
    vendorProfile?.linkedInIntelligence?.profileUrls || ['']
  );
  const [isExtractingLinkedIn, setIsExtractingLinkedIn] = useState<boolean>(false);
  const [linkedinActiveTab, setLinkedinActiveTab] = useState<'company' | 'employees' | 'specialties' | 'updates'>('company');

  // PartnerOn State
  const [partnerOnUrl, setPartnerOnUrl] = useState<string>(
    vendorProfile?.partnerOnUrl || vendorProfile?.partnerOnIntelligence?.partnerOnUrl || ''
  );
  const [isExtractingPartnerOn, setIsExtractingPartnerOn] = useState<boolean>(false);
  const [partnerOnActiveTab, setPartnerOnActiveTab] = useState<'caseStudies' | 'ebooks' | 'solutions' | 'collateral' | 'certifications'>('caseStudies');

  // Common UI State
  const [extractError, setExtractError] = useState<string | null>(null);
  const [extractSuccess, setExtractSuccess] = useState<string | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const websiteIntelligence: WebsiteIntelligence | undefined = vendorProfile?.websiteIntelligence;
  const linkedInIntelligence: LinkedInIntelligence | undefined = vendorProfile?.linkedInIntelligence;
  const partnerOnIntelligence: PartnerOnIntelligence | undefined = vendorProfile?.partnerOnIntelligence;

  // --- Handlers ---

  const handleExtractWebsite = async () => {
    if (!websiteUrl || !websiteUrl.trim()) {
      setExtractError('Please enter a valid website URL (e.g., https://mycompany.com).');
      return;
    }

    setIsExtractingWebsite(true);
    setExtractError(null);
    setExtractSuccess(null);

    try {
      const res = await fetch('/api/extract-website-data', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          url: websiteUrl.trim(),
          companyName: vendorProfile?.companyName || ''
        })
      });

      if (!res.ok) {
        throw new Error(`Extraction server responded with status ${res.status}`);
      }

      const data = await res.json();
      if (!data.success || !data.intelligence) {
        throw new Error(data.error || 'Failed to parse website intelligence.');
      }

      const extractedIntel: WebsiteIntelligence = data.intelligence;

      if (user?.uid) {
        const profileId = vendorProfile?.id || user.uid;
        const payload = {
          website: websiteUrl.trim(),
          websiteIntelligence: extractedIntel,
          updatedAt: new Date()
        };
        await setDoc(doc(db, 'vendor_profiles', profileId), payload, { merge: true });
        await setDoc(doc(db, 'vendors', user.uid), payload, { merge: true });
      }

      if (onProfileUpdated) {
        onProfileUpdated({
          ...vendorProfile,
          website: websiteUrl.trim(),
          websiteIntelligence: extractedIntel
        });
      }

      setExtractSuccess(`Successfully extracted intelligence from ${websiteUrl.trim()}! Data is linked to your RFX proposal drafts.`);
    } catch (err: any) {
      console.error('Error during website extraction:', err);
      setExtractError(err.message || 'Failed to extract website intelligence.');
    } finally {
      setIsExtractingWebsite(false);
    }
  };

  const handleExtractLinkedIn = async () => {
    const validProfileUrls = linkedinProfileUrls.map(u => u.trim()).filter(Boolean);
    if (!linkedinCompanyUrl.trim() && validProfileUrls.length === 0) {
      setExtractError('Please enter a LinkedIn company page URL or at least one LinkedIn profile URL.');
      return;
    }

    setIsExtractingLinkedIn(true);
    setExtractError(null);
    setExtractSuccess(null);

    try {
      const res = await fetch('/api/extract-linkedin-data', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyUrl: linkedinCompanyUrl.trim() || undefined,
          profileUrls: validProfileUrls,
          companyName: vendorProfile?.companyName || ''
        })
      });

      if (!res.ok) {
        throw new Error(`LinkedIn extraction server responded with status ${res.status}`);
      }

      const data = await res.json();
      if (!data.success || !data.intelligence) {
        throw new Error(data.error || 'Failed to extract LinkedIn intelligence.');
      }

      const extractedLinkedIn: LinkedInIntelligence = data.intelligence;

      if (user?.uid) {
        const profileId = vendorProfile?.id || user.uid;
        const payload = {
          linkedinUrl: linkedinCompanyUrl.trim() || validProfileUrls[0] || '',
          linkedInIntelligence: extractedLinkedIn,
          updatedAt: new Date()
        };
        await setDoc(doc(db, 'vendor_profiles', profileId), payload, { merge: true });
        await setDoc(doc(db, 'vendors', user.uid), payload, { merge: true });
      }

      if (onProfileUpdated) {
        onProfileUpdated({
          ...vendorProfile,
          linkedinUrl: linkedinCompanyUrl.trim() || validProfileUrls[0] || '',
          linkedInIntelligence: extractedLinkedIn
        });
      }

      setExtractSuccess('Successfully extracted and verified LinkedIn intelligence! Key leadership profiles and competencies are now integrated.');
    } catch (err: any) {
      console.error('Error during LinkedIn extraction:', err);
      setExtractError(err.message || 'Failed to extract LinkedIn intelligence.');
    } finally {
      setIsExtractingLinkedIn(false);
    }
  };

  const handleExtractPartnerOn = async () => {
    if (!partnerOnUrl || !partnerOnUrl.trim()) {
      setExtractError('Please enter a valid PartnerOn marketing portal URL.');
      return;
    }

    setIsExtractingPartnerOn(true);
    setExtractError(null);
    setExtractSuccess(null);

    try {
      const res = await fetch('/api/extract-partneron-data', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          url: partnerOnUrl.trim(),
          companyName: vendorProfile?.companyName || ''
        })
      });

      if (!res.ok) {
        throw new Error(`PartnerOn extraction server responded with status ${res.status}`);
      }

      const data = await res.json();
      if (!data.success || !data.intelligence) {
        throw new Error(data.error || 'Failed to extract PartnerOn marketing content.');
      }

      const extractedPartnerOn: PartnerOnIntelligence = data.intelligence;

      if (user?.uid) {
        const profileId = vendorProfile?.id || user.uid;
        const payload = {
          partnerOnUrl: partnerOnUrl.trim(),
          partnerOnIntelligence: extractedPartnerOn,
          updatedAt: new Date()
        };
        await setDoc(doc(db, 'vendor_profiles', profileId), payload, { merge: true });
        await setDoc(doc(db, 'vendors', user.uid), payload, { merge: true });
      }

      if (onProfileUpdated) {
        onProfileUpdated({
          ...vendorProfile,
          partnerOnUrl: partnerOnUrl.trim(),
          partnerOnIntelligence: extractedPartnerOn
        });
      }

      setExtractSuccess(`Successfully extracted PartnerOn marketing content! Case studies, solution briefs, and eBooks are now linked to your proposal generator.`);
    } catch (err: any) {
      console.error('Error during PartnerOn extraction:', err);
      setExtractError(err.message || 'Failed to extract PartnerOn content.');
    } finally {
      setIsExtractingPartnerOn(false);
    }
  };

  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(label);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleAddAllNaics = () => {
    if (websiteIntelligence?.recommendedNaicsCodes && onApplyNaics) {
      const rawCodes = websiteIntelligence.recommendedNaicsCodes.map(c => c.split('-')[0].trim());
      onApplyNaics(rawCodes);
      setExtractSuccess('Recommended NAICS codes added to your profile!');
    }
  };

  const handleAddAllCapabilities = (caps?: string[]) => {
    const listToApply = caps || websiteIntelligence?.coreCapabilities || linkedInIntelligence?.specialties;
    if (listToApply && onApplyCapabilities) {
      onApplyCapabilities(listToApply);
      setExtractSuccess('Selected capabilities added to your profile!');
    }
  };

  const handleAddProfileUrl = () => {
    setLinkedinProfileUrls([...linkedinProfileUrls, '']);
  };

  const handleRemoveProfileUrl = (index: number) => {
    const next = linkedinProfileUrls.filter((_, i) => i !== index);
    setLinkedinProfileUrls(next.length === 0 ? [''] : next);
  };

  const handleUpdateProfileUrl = (index: number, val: string) => {
    const next = [...linkedinProfileUrls];
    next[index] = val;
    setLinkedinProfileUrls(next);
  };

  return (
    <div className="bg-white rounded-2xl border border-indigo-100 shadow-sm overflow-hidden space-y-0">
      {/* Header */}
      <div className="p-6 bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 text-white flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="p-1.5 bg-indigo-500/20 text-indigo-300 rounded-lg border border-indigo-400/30">
              <Sparkles className="w-5 h-5 text-indigo-400" />
            </span>
            <h3 className="text-base font-bold tracking-tight text-white flex items-center gap-2">
              Multi-Source Intelligence & Content Hub
            </h3>
          </div>
          <p className="text-xs text-indigo-200/80 max-w-xl">
            Integrate your company website, LinkedIn company page & leadership profiles, and PartnerOn marketing portal to automatically extract case studies, eBooks, solution briefs, and team credentials into your RFX proposal drafts.
          </p>
        </div>

        {/* Integration Status Badges */}
        <div className="flex flex-wrap items-center gap-2">
          {websiteIntelligence && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-500/20 border border-emerald-400/40 text-emerald-300 rounded-full text-[11px] font-semibold">
              <Globe className="w-3 h-3 text-emerald-400" />
              Website Synced
            </span>
          )}
          {linkedInIntelligence && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-500/20 border border-blue-400/40 text-blue-300 rounded-full text-[11px] font-semibold">
              <Linkedin className="w-3 h-3 text-blue-400" />
              LinkedIn Synced
            </span>
          )}
          {partnerOnIntelligence && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-purple-500/20 border border-purple-400/40 text-purple-300 rounded-full text-[11px] font-semibold">
              <Megaphone className="w-3 h-3 text-purple-400" />
              PartnerOn Synced
            </span>
          )}
        </div>
      </div>

      {/* Primary Integration Source Selector */}
      <div className="px-6 pt-4 pb-2 bg-slate-50 border-b border-gray-150 flex flex-wrap gap-3">
        <button
          type="button"
          onClick={() => {
            setSourceMode('website');
            setExtractError(null);
            setExtractSuccess(null);
          }}
          className={`px-4 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center gap-2 cursor-pointer shadow-2xs ${
            sourceMode === 'website'
              ? 'bg-indigo-600 text-white shadow-sm ring-2 ring-indigo-300'
              : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
          }`}
        >
          <Globe className="w-4 h-4" />
          <span>Company Website</span>
          {websiteIntelligence && <span className="w-2 h-2 rounded-full bg-emerald-400" />}
        </button>

        <button
          type="button"
          onClick={() => {
            setSourceMode('linkedin');
            setExtractError(null);
            setExtractSuccess(null);
          }}
          className={`px-4 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center gap-2 cursor-pointer shadow-2xs ${
            sourceMode === 'linkedin'
              ? 'bg-[#0A66C2] text-white shadow-sm ring-2 ring-blue-300'
              : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
          }`}
        >
          <Linkedin className="w-4 h-4" />
          <span>LinkedIn Company & Profiles</span>
          {linkedInIntelligence && <span className="w-2 h-2 rounded-full bg-emerald-400" />}
        </button>

        <button
          type="button"
          onClick={() => {
            setSourceMode('partneron');
            setExtractError(null);
            setExtractSuccess(null);
          }}
          className={`px-4 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center gap-2 cursor-pointer shadow-2xs ${
            sourceMode === 'partneron'
              ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-sm ring-2 ring-purple-300'
              : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
          }`}
        >
          <Megaphone className="w-4 h-4" />
          <span>PartnerOn Marketing Content</span>
          {partnerOnIntelligence && <span className="w-2 h-2 rounded-full bg-emerald-400" />}
        </button>
      </div>

      {/* Global Alerts */}
      <div className="px-6 pt-3">
        {extractError && (
          <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl text-xs text-rose-800 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
            <span>{extractError}</span>
          </div>
        )}

        {extractSuccess && (
          <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-xs text-emerald-800 flex items-center justify-between gap-2">
            <span className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
              {extractSuccess}
            </span>
            <button onClick={() => setExtractSuccess(null)} className="text-emerald-700 font-bold hover:text-emerald-900 cursor-pointer">&times;</button>
          </div>
        )}
      </div>

      {/* ========================================================================= */}
      {/* SOURCE 1: COMPANY WEBSITE */}
      {/* ========================================================================= */}
      {sourceMode === 'website' && (
        <div className="space-y-0 animate-fade-in">
          {/* Website Input Bar */}
          <div className="p-6 bg-slate-50 border-b border-gray-150 space-y-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-400">
                  <Globe className="w-4 h-4 text-indigo-500" />
                </div>
                <input
                  type="text"
                  value={websiteUrl}
                  onChange={(e) => setWebsiteUrl(e.target.value)}
                  placeholder="https://yourcompany.com"
                  className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-xs font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent shadow-2xs"
                />
              </div>

              <button
                type="button"
                onClick={handleExtractWebsite}
                disabled={isExtractingWebsite || !websiteUrl.trim()}
                className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-xs font-bold rounded-xl transition-all flex items-center justify-center gap-2 shadow-xs cursor-pointer"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${isExtractingWebsite ? 'animate-spin' : ''}`} />
                <span>{isExtractingWebsite ? 'Extracting Intelligence...' : 'Extract from Website'}</span>
              </button>
            </div>
          </div>

          {/* Extracted Website Intelligence Display */}
          {websiteIntelligence ? (
            <div className="p-6 space-y-6">
              {/* Tabs */}
              <div className="flex flex-wrap border-b border-gray-150 gap-2 pb-2">
                <button
                  onClick={() => setWebsiteActiveTab('overview')}
                  className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all cursor-pointer ${
                    websiteActiveTab === 'overview' ? 'bg-indigo-50 text-indigo-700 border border-indigo-200' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Overview & Win Themes
                </button>
                <button
                  onClick={() => setWebsiteActiveTab('capabilities')}
                  className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all cursor-pointer ${
                    websiteActiveTab === 'capabilities' ? 'bg-indigo-50 text-indigo-700 border border-indigo-200' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Capabilities ({websiteIntelligence.coreCapabilities?.length || 0})
                </button>
                <button
                  onClick={() => setWebsiteActiveTab('pastPerformance')}
                  className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all cursor-pointer ${
                    websiteActiveTab === 'pastPerformance' ? 'bg-indigo-50 text-indigo-700 border border-indigo-200' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Past Performance ({websiteIntelligence.pastPerformance?.length || 0})
                </button>
                <button
                  onClick={() => setWebsiteActiveTab('leadership')}
                  className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all cursor-pointer ${
                    websiteActiveTab === 'leadership' ? 'bg-indigo-50 text-indigo-700 border border-indigo-200' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Key Personnel ({websiteIntelligence.leadershipAndTeam?.length || 0})
                </button>
                <button
                  onClick={() => setWebsiteActiveTab('codes')}
                  className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all cursor-pointer ${
                    websiteActiveTab === 'codes' ? 'bg-indigo-50 text-indigo-700 border border-indigo-200' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Codes & Compliance
                </button>
              </div>

              {/* Tab 1: Overview */}
              {websiteActiveTab === 'overview' && (
                <div className="space-y-6 animate-fade-in">
                  <div className="bg-slate-50 p-4 rounded-xl border border-gray-150 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-bold text-indigo-600 uppercase tracking-wider">
                        Corporate Summary (from Website)
                      </span>
                      <button
                        onClick={() => handleCopy(websiteIntelligence.summary || '', 'summary')}
                        className="text-[10px] text-gray-500 hover:text-indigo-600 flex items-center gap-1 font-semibold cursor-pointer"
                      >
                        {copiedField === 'summary' ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
                        <span>{copiedField === 'summary' ? 'Copied' : 'Copy'}</span>
                      </button>
                    </div>
                    <p className="text-xs text-gray-700 leading-relaxed">{websiteIntelligence.summary}</p>
                    {websiteIntelligence.tagline && (
                      <p className="text-xs font-semibold text-indigo-900 italic pt-1 border-t border-gray-200/60">
                        &ldquo;{websiteIntelligence.tagline}&rdquo;
                      </p>
                    )}
                  </div>

                  {/* Differentiators */}
                  <div className="space-y-3">
                    <h4 className="text-xs font-bold text-gray-900 uppercase tracking-wider flex items-center gap-2">
                      <Award className="w-4 h-4 text-amber-500" />
                      Competitive Differentiators & Win Themes
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                      {websiteIntelligence.differentiators?.map((diff, i) => (
                        <div key={i} className="p-3 bg-amber-50/50 border border-amber-200/60 rounded-xl flex items-start gap-2.5">
                          <span className="w-5 h-5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5">
                            {i + 1}
                          </span>
                          <span className="text-xs font-medium text-amber-950">{diff}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Products and Services */}
                  {websiteIntelligence.productsAndServices && websiteIntelligence.productsAndServices.length > 0 && (
                    <div className="space-y-3">
                      <h4 className="text-xs font-bold text-gray-900 uppercase tracking-wider flex items-center gap-2">
                        <Briefcase className="w-4 h-4 text-indigo-600" />
                        Flagship Offerings & Solution Frameworks
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {websiteIntelligence.productsAndServices.map((prod, i) => (
                          <div key={i} className="p-3.5 bg-gray-50 border border-gray-200 rounded-xl space-y-1">
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-bold text-gray-900">{prod.name}</span>
                              {prod.category && (
                                <span className="text-[10px] bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded font-semibold">
                                  {prod.category}
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-600">{prod.description}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Tab 2: Capabilities */}
              {websiteActiveTab === 'capabilities' && (
                <div className="space-y-4 animate-fade-in">
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-gray-500">
                      Extracted core competencies grounded in your public web presence.
                    </p>
                    {onApplyCapabilities && (
                      <button
                        type="button"
                        onClick={() => handleAddAllCapabilities()}
                        className="px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-bold text-xs rounded-lg border border-indigo-200 flex items-center gap-1.5 transition-colors cursor-pointer"
                      >
                        <Plus className="w-3.5 h-3.5" />
                        Apply All to Vendor Profile
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2.5">
                    {websiteIntelligence.coreCapabilities?.map((cap, i) => (
                      <div key={i} className="p-3 bg-slate-50 border border-slate-200 rounded-xl flex items-center gap-2.5">
                        <div className="w-2 h-2 rounded-full bg-indigo-600 shrink-0" />
                        <span className="text-xs font-medium text-gray-800">{cap}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Tab 3: Past Performance */}
              {websiteActiveTab === 'pastPerformance' && (
                <div className="space-y-4 animate-fade-in">
                  <p className="text-xs text-gray-500">
                    Extracted case studies and client projects referenced in Volume 1 (Past Performance Section).
                  </p>
                  <div className="space-y-3">
                    {websiteIntelligence.pastPerformance?.map((perf, i) => (
                      <div key={i} className="p-4 bg-gray-50 border border-gray-200 rounded-xl space-y-2">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <span className="text-xs font-bold text-gray-900 flex items-center gap-1.5">
                            <Building2 className="w-3.5 h-3.5 text-indigo-600" />
                            {perf.clientOrProject}
                          </span>
                          <div className="flex items-center gap-2">
                            {perf.period && (
                              <span className="text-[10px] font-mono text-gray-500 bg-white px-2 py-0.5 rounded border border-gray-200">
                                {perf.period}
                              </span>
                            )}
                            {perf.contractValue && (
                              <span className="text-[10px] font-mono font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                                {perf.contractValue}
                              </span>
                            )}
                          </div>
                        </div>
                        <p className="text-xs text-gray-700">{perf.description}</p>
                        {perf.outcomes && (
                          <div className="pt-1.5 border-t border-gray-200/60 flex items-start gap-1.5 text-xs text-emerald-800 font-medium">
                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0 mt-0.5" />
                            <span>Outcome: {perf.outcomes}</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Tab 4: Leadership */}
              {websiteActiveTab === 'leadership' && (
                <div className="space-y-4 animate-fade-in">
                  <p className="text-xs text-gray-500">
                    Key leaders and technical specialists attached to proposal staffing and key personnel schedules.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {websiteIntelligence.leadershipAndTeam?.map((person, i) => (
                      <div key={i} className="p-4 bg-gray-50 border border-gray-200 rounded-xl space-y-1.5">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-bold text-gray-900">{person.name}</span>
                          <span className="text-[10px] font-semibold text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded">
                            {person.role}
                          </span>
                        </div>
                        <p className="text-xs text-gray-600 leading-relaxed">{person.bio}</p>
                        {person.experienceYears && (
                          <p className="text-[10px] font-mono text-gray-400">
                            Experience: {person.experienceYears}+ years in enterprise & public sector delivery
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Tab 5: Codes & Compliance */}
              {websiteActiveTab === 'codes' && (
                <div className="space-y-6 animate-fade-in">
                  {/* NAICS Codes */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="text-xs font-bold text-gray-900 uppercase tracking-wider flex items-center gap-1.5">
                        <FileCode2 className="w-4 h-4 text-indigo-600" />
                        Recommended NAICS Codes
                      </h4>
                      {onApplyNaics && (
                        <button
                          type="button"
                          onClick={handleAddAllNaics}
                          className="text-xs font-bold text-indigo-600 hover:text-indigo-800 flex items-center gap-1 cursor-pointer"
                        >
                          <Plus className="w-3.5 h-3.5" />
                          Add to Profile
                        </button>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {websiteIntelligence.recommendedNaicsCodes?.map((code, i) => (
                        <span key={i} className="px-2.5 py-1 bg-indigo-50 text-indigo-800 border border-indigo-200 rounded-lg text-xs font-semibold font-mono">
                          {code}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Certifications */}
                  <div className="space-y-3">
                    <h4 className="text-xs font-bold text-gray-900 uppercase tracking-wider flex items-center gap-1.5">
                      <ShieldCheck className="w-4 h-4 text-emerald-600" />
                      Extracted Certifications & Compliance
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {websiteIntelligence.certificationsAndCompliance?.map((cert, i) => (
                        <span key={i} className="px-3 py-1 bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-lg text-xs font-semibold flex items-center gap-1.5">
                          <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                          {cert}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="p-8 text-center space-y-3 text-gray-400">
              <Globe className="w-10 h-10 mx-auto text-gray-300" />
              <p className="text-xs font-medium text-gray-600">No website intelligence extracted yet.</p>
              <p className="text-xs text-gray-400 max-w-md mx-auto">
                Enter your company website above and click &quot;Extract from Website&quot; to automatically enrich your proposal generation profile.
              </p>
            </div>
          )}
        </div>
      )}

      {/* ========================================================================= */}
      {/* SOURCE 2: LINKEDIN COMPANY & PROFILES */}
      {/* ========================================================================= */}
      {sourceMode === 'linkedin' && (
        <div className="space-y-0 animate-fade-in">
          {/* LinkedIn Input Form */}
          <div className="p-6 bg-slate-50 border-b border-gray-150 space-y-4">
            <div className="space-y-3">
              <div>
                <label className="text-[11px] font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5 mb-1">
                  <Linkedin className="w-3.5 h-3.5 text-[#0A66C2]" />
                  LinkedIn Company Page URL
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={linkedinCompanyUrl}
                    onChange={(e) => setLinkedinCompanyUrl(e.target.value)}
                    placeholder="https://linkedin.com/company/your-organization"
                    className="w-full pl-3.5 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-xs font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#0A66C2] focus:border-transparent shadow-2xs"
                  />
                </div>
              </div>

              {/* Executive / Employee Profile URLs */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-[11px] font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
                    <Users className="w-3.5 h-3.5 text-[#0A66C2]" />
                    Key Leadership & Employee Profile URLs
                  </label>
                  <button
                    type="button"
                    onClick={handleAddProfileUrl}
                    className="text-[11px] font-bold text-[#0A66C2] hover:text-blue-800 flex items-center gap-1 cursor-pointer"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Add Another Profile URL
                  </button>
                </div>

                <div className="space-y-2">
                  {linkedinProfileUrls.map((pUrl, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <input
                        type="text"
                        value={pUrl}
                        onChange={(e) => handleUpdateProfileUrl(idx, e.target.value)}
                        placeholder={`https://linkedin.com/in/key-leader-profile-${idx + 1}`}
                        className="flex-1 px-3.5 py-2 bg-white border border-gray-200 rounded-xl text-xs font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#0A66C2] shadow-2xs"
                      />
                      {linkedinProfileUrls.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveProfileUrl(idx)}
                          className="p-2 text-gray-400 hover:text-rose-600 rounded-lg hover:bg-rose-50 transition-colors cursor-pointer"
                          title="Remove URL"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <button
                type="button"
                onClick={handleExtractLinkedIn}
                disabled={isExtractingLinkedIn || (!linkedinCompanyUrl.trim() && linkedinProfileUrls.every(u => !u.trim()))}
                className="px-6 py-2.5 bg-[#0A66C2] hover:bg-[#004182] disabled:opacity-50 text-white text-xs font-bold rounded-xl transition-all flex items-center justify-center gap-2 shadow-sm cursor-pointer"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${isExtractingLinkedIn ? 'animate-spin' : ''}`} />
                <span>{isExtractingLinkedIn ? 'Extracting LinkedIn Intelligence...' : 'Extract LinkedIn Intelligence'}</span>
              </button>
            </div>
          </div>

          {/* Extracted LinkedIn Intelligence Display */}
          {linkedInIntelligence ? (
            <div className="p-6 space-y-6">
              {/* Sub-tabs */}
              <div className="flex flex-wrap border-b border-gray-150 gap-2 pb-2">
                <button
                  onClick={() => setLinkedinActiveTab('company')}
                  className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all cursor-pointer ${
                    linkedinActiveTab === 'company' ? 'bg-blue-50 text-[#0A66C2] border border-blue-200' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Company Profile & Overview
                </button>
                <button
                  onClick={() => setLinkedinActiveTab('employees')}
                  className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all cursor-pointer ${
                    linkedinActiveTab === 'employees' ? 'bg-blue-50 text-[#0A66C2] border border-blue-200' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Verified Key Leadership ({linkedInIntelligence.employeeProfiles?.length || 0})
                </button>
                <button
                  onClick={() => setLinkedinActiveTab('specialties')}
                  className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all cursor-pointer ${
                    linkedinActiveTab === 'specialties' ? 'bg-blue-50 text-[#0A66C2] border border-blue-200' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Specialties & Target Competencies ({linkedInIntelligence.specialties?.length || 0})
                </button>
                <button
                  onClick={() => setLinkedinActiveTab('updates')}
                  className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all cursor-pointer ${
                    linkedinActiveTab === 'updates' ? 'bg-blue-50 text-[#0A66C2] border border-blue-200' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Post Highlights & Updates ({linkedInIntelligence.recentUpdates?.length || 0})
                </button>
              </div>

              {/* Tab 1: Company Profile */}
              {linkedinActiveTab === 'company' && (
                <div className="space-y-6 animate-fade-in">
                  <div className="bg-blue-50/40 p-5 rounded-2xl border border-blue-150 space-y-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-blue-200/60 pb-3">
                      <div>
                        <h4 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                          <Linkedin className="w-4 h-4 text-[#0A66C2]" />
                          {linkedInIntelligence.companyName || vendorProfile?.companyName || 'Corporate Entity'}
                        </h4>
                        {linkedInIntelligence.headline && (
                          <p className="text-xs text-blue-900 font-medium mt-0.5">{linkedInIntelligence.headline}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {linkedInIntelligence.companySize && (
                          <span className="text-[10px] font-semibold bg-white border border-blue-200 text-blue-800 px-2.5 py-1 rounded-full">
                            {linkedInIntelligence.companySize}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <span className="text-[10px] font-bold text-[#0A66C2] uppercase tracking-wider">
                        Executive Overview & Gov-Contracting Focus
                      </span>
                      <p className="text-xs text-gray-700 leading-relaxed">{linkedInIntelligence.overview}</p>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2">
                      {linkedInIntelligence.industry && (
                        <div className="p-2.5 bg-white rounded-xl border border-blue-100">
                          <span className="text-[9px] font-bold text-gray-400 uppercase block">Industry</span>
                          <span className="text-xs font-semibold text-gray-800">{linkedInIntelligence.industry}</span>
                        </div>
                      )}
                      {linkedInIntelligence.headquarters && (
                        <div className="p-2.5 bg-white rounded-xl border border-blue-100">
                          <span className="text-[9px] font-bold text-gray-400 uppercase block">Headquarters</span>
                          <span className="text-xs font-semibold text-gray-800">{linkedInIntelligence.headquarters}</span>
                        </div>
                      )}
                      {linkedInIntelligence.founded && (
                        <div className="p-2.5 bg-white rounded-xl border border-blue-100">
                          <span className="text-[9px] font-bold text-gray-400 uppercase block">Founded</span>
                          <span className="text-xs font-semibold text-gray-800">{linkedInIntelligence.founded}</span>
                        </div>
                      )}
                      {linkedInIntelligence.companySize && (
                        <div className="p-2.5 bg-white rounded-xl border border-blue-100">
                          <span className="text-[9px] font-bold text-gray-400 uppercase block">Organization Size</span>
                          <span className="text-xs font-semibold text-gray-800">{linkedInIntelligence.companySize}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Tab 2: Verified Key Leadership */}
              {linkedinActiveTab === 'employees' && (
                <div className="space-y-4 animate-fade-in">
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-gray-500">
                      Key executives and technical SMEs extracted from LinkedIn to ground proposal Key Personnel resumes (Volume 1).
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {linkedInIntelligence.employeeProfiles?.map((emp, i) => (
                      <div key={i} className="p-4 bg-slate-50 border border-slate-200 rounded-2xl space-y-3">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <h5 className="text-xs font-bold text-gray-900 flex items-center gap-1.5">
                              <Linkedin className="w-3.5 h-3.5 text-[#0A66C2]" />
                              {emp.name}
                            </h5>
                            <p className="text-[11px] font-semibold text-[#0A66C2]">{emp.title}</p>
                          </div>
                          {emp.profileUrl && (
                            <a
                              href={emp.profileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[10px] text-gray-500 hover:text-[#0A66C2] flex items-center gap-1 bg-white px-2 py-1 rounded-lg border border-gray-200"
                            >
                              <span>View Profile</span>
                              <ExternalLink className="w-2.5 h-2.5" />
                            </a>
                          )}
                        </div>

                        {emp.headline && (
                          <p className="text-[11px] text-gray-600 italic bg-white p-2 rounded-xl border border-gray-150">
                            &ldquo;{emp.headline}&rdquo;
                          </p>
                        )}

                        <p className="text-xs text-gray-700 leading-relaxed">{emp.summary}</p>

                        {/* Skills */}
                        {emp.skills && emp.skills.length > 0 && (
                          <div className="space-y-1.5 pt-1">
                            <span className="text-[9px] font-bold text-gray-400 uppercase">Core Skills</span>
                            <div className="flex flex-wrap gap-1.5">
                              {emp.skills.map((sk, skIdx) => (
                                <span key={skIdx} className="px-2 py-0.5 bg-blue-50 text-[#0A66C2] rounded-md text-[10px] font-medium border border-blue-150">
                                  {sk}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Credentials */}
                        <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-gray-200/60 text-[10px] text-gray-500">
                          {emp.experience && (
                            <span className="flex items-center gap-1">
                              <Briefcase className="w-3 h-3 text-gray-400" />
                              {emp.experience}
                            </span>
                          )}
                          {emp.education && (
                            <span className="flex items-center gap-1">
                              <GraduationCap className="w-3 h-3 text-gray-400" />
                              {emp.education}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Tab 3: Specialties & Competencies */}
              {linkedinActiveTab === 'specialties' && (
                <div className="space-y-6 animate-fade-in">
                  {/* Specialties */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="text-xs font-bold text-gray-900 uppercase tracking-wider flex items-center gap-1.5">
                        <Award className="w-4 h-4 text-[#0A66C2]" />
                        LinkedIn Verified Organization Specialties
                      </h4>
                      {onApplyCapabilities && linkedInIntelligence.specialties && (
                        <button
                          type="button"
                          onClick={() => handleAddAllCapabilities(linkedInIntelligence.specialties)}
                          className="px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-[#0A66C2] font-bold text-xs rounded-lg border border-blue-200 flex items-center gap-1.5 transition-colors cursor-pointer"
                        >
                          <Plus className="w-3.5 h-3.5" />
                          Apply All to Capabilities
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2.5">
                      {linkedInIntelligence.specialties?.map((spec, i) => (
                        <div key={i} className="p-3 bg-blue-50/50 border border-blue-150 rounded-xl flex items-center gap-2.5">
                          <span className="w-2 h-2 rounded-full bg-[#0A66C2]" />
                          <span className="text-xs font-medium text-blue-950">{spec}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Target Competencies */}
                  {linkedInIntelligence.targetKeyCompetencies && linkedInIntelligence.targetKeyCompetencies.length > 0 && (
                    <div className="space-y-3 pt-2">
                      <h4 className="text-xs font-bold text-gray-900 uppercase tracking-wider flex items-center gap-1.5">
                        <TrendingUp className="w-4 h-4 text-emerald-600" />
                        Target RFX Alignment Competencies
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {linkedInIntelligence.targetKeyCompetencies.map((comp, i) => (
                          <span key={i} className="px-3 py-1.5 bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-xl text-xs font-semibold flex items-center gap-1.5">
                            <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                            {comp}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Tab 4: Post Highlights */}
              {linkedinActiveTab === 'updates' && (
                <div className="space-y-4 animate-fade-in">
                  <p className="text-xs text-gray-500">
                    Recent company post highlights and thought leadership milestones extracted from LinkedIn.
                  </p>
                  <div className="space-y-3">
                    {linkedInIntelligence.recentUpdates?.map((post, i) => (
                      <div key={i} className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-2">
                        <div className="flex items-center justify-between">
                          <h5 className="text-xs font-bold text-gray-900">{post.title}</h5>
                          {post.date && (
                            <span className="text-[10px] text-gray-400 font-medium">{post.date}</span>
                          )}
                        </div>
                        <p className="text-xs text-gray-700">{post.snippet}</p>
                        <div className="flex items-center justify-between pt-2 border-t border-gray-200/60 text-[10px]">
                          {post.engagement && (
                            <span className="text-[#0A66C2] font-semibold">{post.engagement}</span>
                          )}
                          {post.url && (
                            <a
                              href={post.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-gray-500 hover:text-[#0A66C2] flex items-center gap-1 font-medium"
                            >
                              <span>View Post</span>
                              <ExternalLink className="w-2.5 h-2.5" />
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="p-8 text-center space-y-3 text-gray-400">
              <Linkedin className="w-10 h-10 mx-auto text-blue-400 opacity-60" />
              <p className="text-xs font-medium text-gray-600">No LinkedIn intelligence extracted yet.</p>
              <p className="text-xs text-gray-400 max-w-md mx-auto">
                Enter your company&apos;s LinkedIn page or key personnel profile URLs above and click &quot;Extract LinkedIn Intelligence&quot; to auto-populate team resumes and verified specialties.
              </p>
            </div>
          )}
        </div>
      )}

      {/* ========================================================================= */}
      {/* SOURCE 3: PARTNERON MARKETING CONTENT */}
      {/* ========================================================================= */}
      {sourceMode === 'partneron' && (
        <div className="space-y-0 animate-fade-in">
          {/* PartnerOn Input Bar */}
          <div className="p-6 bg-slate-50 border-b border-gray-150 space-y-4">
            <div className="space-y-2">
              <label className="text-[11px] font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
                <Megaphone className="w-3.5 h-3.5 text-purple-600" />
                PartnerOn Account / Syndicated Marketing Website URL
              </label>
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <input
                    type="text"
                    value={partnerOnUrl}
                    onChange={(e) => setPartnerOnUrl(e.target.value)}
                    placeholder="https://partneron.com/portal/your-partner-account"
                    className="w-full pl-3.5 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-xs font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent shadow-2xs"
                  />
                </div>

                <button
                  type="button"
                  onClick={handleExtractPartnerOn}
                  disabled={isExtractingPartnerOn || !partnerOnUrl.trim()}
                  className="px-6 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 disabled:opacity-50 text-white text-xs font-bold rounded-xl transition-all flex items-center justify-center gap-2 shadow-xs cursor-pointer"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${isExtractingPartnerOn ? 'animate-spin' : ''}`} />
                  <span>{isExtractingPartnerOn ? 'Extracting Content...' : 'Extract PartnerOn Content'}</span>
                </button>
              </div>
            </div>
          </div>

          {/* Extracted PartnerOn Intelligence Display */}
          {partnerOnIntelligence ? (
            <div className="p-6 space-y-6">
              {/* Partner Tier Header Banner */}
              <div className="p-4 bg-gradient-to-r from-purple-50 via-indigo-50 to-purple-50 border border-purple-200 rounded-2xl flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="px-2.5 py-0.5 bg-purple-600 text-white rounded-full text-[10px] font-extrabold uppercase tracking-wide">
                      Partner Tier
                    </span>
                    <h4 className="text-xs font-extrabold text-purple-950">
                      {partnerOnIntelligence.partnerTier || 'Authorized Partner Solutions Hub'}
                    </h4>
                  </div>
                  <p className="text-xs text-purple-900/80 max-w-2xl leading-relaxed">
                    {partnerOnIntelligence.summary}
                  </p>
                </div>
              </div>

              {/* Sub-tabs */}
              <div className="flex flex-wrap border-b border-gray-150 gap-2 pb-2">
                <button
                  onClick={() => setPartnerOnActiveTab('caseStudies')}
                  className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all cursor-pointer ${
                    partnerOnActiveTab === 'caseStudies' ? 'bg-purple-50 text-purple-700 border border-purple-200' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Case Studies ({partnerOnIntelligence.caseStudies?.length || 0})
                </button>
                <button
                  onClick={() => setPartnerOnActiveTab('ebooks')}
                  className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all cursor-pointer ${
                    partnerOnActiveTab === 'ebooks' ? 'bg-purple-50 text-purple-700 border border-purple-200' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  eBooks & Whitepapers ({partnerOnIntelligence.eBooksAndWhitepapers?.length || 0})
                </button>
                <button
                  onClick={() => setPartnerOnActiveTab('solutions')}
                  className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all cursor-pointer ${
                    partnerOnActiveTab === 'solutions' ? 'bg-purple-50 text-purple-700 border border-purple-200' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Solution Briefs ({partnerOnIntelligence.solutionBriefs?.length || 0})
                </button>
                <button
                  onClick={() => setPartnerOnActiveTab('collateral')}
                  className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all cursor-pointer ${
                    partnerOnActiveTab === 'collateral' ? 'bg-purple-50 text-purple-700 border border-purple-200' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Marketing Collateral ({partnerOnIntelligence.marketingCollateral?.length || 0})
                </button>
                <button
                  onClick={() => setPartnerOnActiveTab('certifications')}
                  className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all cursor-pointer ${
                    partnerOnActiveTab === 'certifications' ? 'bg-purple-50 text-purple-700 border border-purple-200' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Authorizations & Badges ({partnerOnIntelligence.certificationsAndAuthorizations?.length || 0})
                </button>
              </div>

              {/* Tab 1: Case Studies */}
              {partnerOnActiveTab === 'caseStudies' && (
                <div className="space-y-4 animate-fade-in">
                  <p className="text-xs text-gray-500">
                    Customer success stories with quantified outcomes extracted from PartnerOn syndication to inject into RFP Past Performance matrices.
                  </p>

                  <div className="space-y-4">
                    {partnerOnIntelligence.caseStudies?.map((cs, i) => (
                      <div key={i} className="p-5 bg-slate-50 border border-slate-200 rounded-2xl space-y-3">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-gray-200/60 pb-2.5">
                          <div>
                            <span className="text-[10px] font-bold text-purple-600 uppercase tracking-wider block">
                              {cs.clientOrIndustry || 'Public Sector Case Study'}
                            </span>
                            <h5 className="text-xs font-bold text-gray-900">{cs.title}</h5>
                          </div>
                          {cs.assetUrl && (
                            <a
                              href={cs.assetUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[10px] font-semibold text-purple-700 hover:text-purple-900 flex items-center gap-1 bg-white px-2.5 py-1 rounded-lg border border-purple-200 self-start shrink-0"
                            >
                              <Download className="w-3 h-3" />
                              <span>Download PDF</span>
                            </a>
                          )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                          <div className="p-3 bg-white rounded-xl border border-gray-150 space-y-1">
                            <span className="text-[10px] font-bold text-amber-700 uppercase block">The Challenge</span>
                            <p className="text-gray-600">{cs.challenge}</p>
                          </div>
                          <div className="p-3 bg-white rounded-xl border border-gray-150 space-y-1">
                            <span className="text-[10px] font-bold text-indigo-700 uppercase block">The Solution</span>
                            <p className="text-gray-600">{cs.solution}</p>
                          </div>
                        </div>

                        {cs.keyOutcomes && (
                          <div className="p-3 bg-emerald-50/70 border border-emerald-200 rounded-xl flex items-start gap-2 text-xs text-emerald-900">
                            <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                            <div>
                              <strong className="font-bold">Quantified Business Outcomes: </strong>
                              <span>{cs.keyOutcomes}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Tab 2: eBooks & Whitepapers */}
              {partnerOnActiveTab === 'ebooks' && (
                <div className="space-y-4 animate-fade-in">
                  <p className="text-xs text-gray-500">
                    Comprehensive strategic guides, architecture whitepapers, and thought leadership assets.
                  </p>

                  <div className="space-y-4">
                    {partnerOnIntelligence.eBooksAndWhitepapers?.map((eb, i) => (
                      <div key={i} className="p-5 bg-slate-50 border border-slate-200 rounded-2xl space-y-3">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                          <div className="space-y-0.5">
                            <span className="text-[10px] font-bold text-indigo-600 uppercase tracking-wider">
                              {eb.topic}
                            </span>
                            <h5 className="text-xs font-bold text-gray-900">{eb.title}</h5>
                          </div>
                          <div className="flex items-center gap-2">
                            {eb.pagesOrDuration && (
                              <span className="text-[10px] font-mono text-gray-500 bg-white px-2 py-0.5 rounded border border-gray-200">
                                {eb.pagesOrDuration}
                              </span>
                            )}
                            {eb.downloadUrl && (
                              <a
                                href={eb.downloadUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[10px] font-semibold text-indigo-600 hover:text-indigo-800 flex items-center gap-1 bg-white px-2.5 py-1 rounded-lg border border-indigo-200"
                              >
                                <Download className="w-3 h-3" />
                                <span>Download</span>
                              </a>
                            )}
                          </div>
                        </div>

                        <p className="text-xs text-gray-700 leading-relaxed bg-white p-3.5 rounded-xl border border-gray-150">
                          {eb.executiveSummary}
                        </p>

                        {eb.keyTakeaways && eb.keyTakeaways.length > 0 && (
                          <div className="space-y-1.5 pt-1">
                            <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                              Strategic Key Takeaways
                            </span>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                              {eb.keyTakeaways.map((item, itIdx) => (
                                <div key={itIdx} className="p-2.5 bg-indigo-50/50 rounded-xl border border-indigo-150/60 flex items-start gap-2">
                                  <BookmarkCheck className="w-3.5 h-3.5 text-indigo-600 shrink-0 mt-0.5" />
                                  <span className="text-xs text-indigo-950">{item}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Tab 3: Solution Briefs */}
              {partnerOnActiveTab === 'solutions' && (
                <div className="space-y-4 animate-fade-in">
                  <p className="text-xs text-gray-500">
                    Productized solutions, technical accelerators, and service blueprints ready for proposal Statement of Work injection.
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {partnerOnIntelligence.solutionBriefs?.map((sol, i) => (
                      <div key={i} className="p-4 bg-slate-50 border border-slate-200 rounded-2xl space-y-3">
                        <div className="space-y-1">
                          <span className="text-[10px] font-bold text-purple-600 uppercase tracking-wider block">
                            {sol.focusArea}
                          </span>
                          <h5 className="text-xs font-bold text-gray-900">{sol.title}</h5>
                          {sol.targetAudience && (
                            <span className="text-[10px] text-gray-500 block">
                              Audience: <strong>{sol.targetAudience}</strong>
                            </span>
                          )}
                        </div>

                        <p className="text-xs text-gray-600 leading-relaxed">{sol.description}</p>

                        {sol.technicalHighlights && sol.technicalHighlights.length > 0 && (
                          <div className="space-y-1.5 pt-2 border-t border-gray-200/60">
                            <span className="text-[9px] font-bold text-gray-400 uppercase">Technical Architecture Highlights</span>
                            <div className="space-y-1">
                              {sol.technicalHighlights.map((th, thIdx) => (
                                <div key={thIdx} className="flex items-center gap-1.5 text-xs text-gray-700">
                                  <span className="w-1.5 h-1.5 rounded-full bg-purple-500 shrink-0" />
                                  <span>{th}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Tab 4: Marketing Collateral */}
              {partnerOnActiveTab === 'collateral' && (
                <div className="space-y-4 animate-fade-in">
                  <p className="text-xs text-gray-500">
                    Print-ready capabilities statements, security whitecards, and co-branded sales presentations.
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {partnerOnIntelligence.marketingCollateral?.map((col, i) => (
                      <div key={i} className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-bold text-gray-900">{col.name}</span>
                          <span className="text-[10px] font-mono bg-purple-50 text-purple-700 px-2 py-0.5 rounded font-semibold">
                            {col.format || col.assetType}
                          </span>
                        </div>
                        <p className="text-xs text-gray-600">{col.description}</p>
                        {col.url && (
                          <a
                            href={col.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[10px] font-semibold text-purple-600 hover:text-purple-800 flex items-center gap-1 pt-1"
                          >
                            <Download className="w-3 h-3" />
                            <span>View / Download Asset</span>
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Tab 5: Authorizations & Badges */}
              {partnerOnActiveTab === 'certifications' && (
                <div className="space-y-4 animate-fade-in">
                  <h4 className="text-xs font-bold text-gray-900 uppercase tracking-wider flex items-center gap-1.5">
                    <ShieldCheck className="w-4 h-4 text-purple-600" />
                    Verified Partner Tier Badges & OEM Authorizations
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    {partnerOnIntelligence.certificationsAndAuthorizations?.map((cert, i) => (
                      <div key={i} className="p-3 bg-purple-50/50 border border-purple-150 rounded-xl flex items-center gap-2.5">
                        <CheckCircle2 className="w-4 h-4 text-purple-600 shrink-0" />
                        <span className="text-xs font-semibold text-purple-950">{cert}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="p-8 text-center space-y-3 text-gray-400">
              <Megaphone className="w-10 h-10 mx-auto text-purple-300" />
              <p className="text-xs font-medium text-gray-600">No PartnerOn marketing content extracted yet.</p>
              <p className="text-xs text-gray-400 max-w-md mx-auto">
                Enter your PartnerOn account or syndicated marketing portal URL above and click &quot;Extract PartnerOn Content&quot; to import case studies, whitepapers, and solution briefs.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
